// Pure logic for the Word Counter's extra panels: platform limits, a word goal, the sentence-length view and a reading ease score.
// No React here. Everything is computed from the text in the page; nothing is stored or sent anywhere.
import type { CalcStep } from "@/lib/calc/steps";
import { countSyllables, easeLabel, gradeLabel } from "./readability";
import { splitSentenceTexts } from "./wordCounterCore";

// ---------------- Platform limits ----------------

export interface PlatformLimit { id: string; label: string; limit: number; /** "chars" or "words" */ unit: "chars"; /** Soft limits are a display guide, not a hard stop. */ soft?: boolean }

/**
 * Character limits people check most. Platforms change them, so the page says to confirm on the platform.
 * X counts every link as 23 characters and some characters (emoji, CJK) as two; this table counts plain characters.
 */
export const PLATFORM_LIMITS: PlatformLimit[] = [
  { id: "x", label: "X (Twitter) post", limit: 280, unit: "chars" },
  { id: "bluesky", label: "Bluesky post", limit: 300, unit: "chars" },
  { id: "threads", label: "Threads post", limit: 500, unit: "chars" },
  { id: "instagram", label: "Instagram caption", limit: 2200, unit: "chars" },
  { id: "linkedin", label: "LinkedIn post", limit: 3000, unit: "chars" },
  { id: "pinterest", label: "Pinterest pin description", limit: 500, unit: "chars" },
  { id: "yt-title", label: "YouTube title", limit: 100, unit: "chars" },
  { id: "yt-desc", label: "YouTube description", limit: 5000, unit: "chars" },
  { id: "meta-title", label: "SEO title (about)", limit: 60, unit: "chars", soft: true },
  { id: "meta-desc", label: "Meta description (about)", limit: 160, unit: "chars", soft: true },
  { id: "sms", label: "SMS (one segment)", limit: 160, unit: "chars", soft: true },
];

export interface LimitStatus { id: string; label: string; limit: number; used: number; over: boolean; remaining: number; text: string; soft: boolean }

/** Over/under for each limit. `text` is the plain-words status (never colour alone): "12 left" or "5 over". */
export function limitStatuses(chars: number, limits: PlatformLimit[] = PLATFORM_LIMITS): LimitStatus[] {
  return limits.map((l) => {
    const remaining = l.limit - chars;
    const over = remaining < 0;
    return { id: l.id, label: l.label, limit: l.limit, used: chars, over, remaining, soft: !!l.soft, text: over ? `${-remaining} over` : `${remaining} left` };
  });
}

// ---------------- Word goal ----------------

export interface GoalProgress { target: number; words: number; percent: number; remaining: number; reached: boolean; text: string }

/** Progress to a target word count. percent is capped at 100 for the bar; text says how far over when passed. */
export function goalProgress(words: number, target: number): GoalProgress | null {
  if (!Number.isFinite(target) || target < 1) return null;
  const t = Math.floor(target);
  const percent = Math.min(100, Math.round((words / t) * 100));
  const remaining = t - words;
  return { target: t, words, percent, remaining, reached: words >= t, text: remaining > 0 ? `${remaining} words to go` : remaining === 0 ? "Goal reached" : `${-remaining} words over the goal` };
}

// ---------------- Sentence length view ----------------

export const LENGTH_BUCKETS = [
  { id: "b1", label: "1 word", min: 1, max: 1 },
  { id: "b2", label: "2 to 6 words", min: 2, max: 6 },
  { id: "b3", label: "7 to 15 words", min: 7, max: 15 },
  { id: "b4", label: "16 to 25 words", min: 16, max: 25 },
  { id: "b5", label: "26 to 39 words", min: 26, max: 39 },
  { id: "b6", label: "40 or more words", min: 40, max: Infinity },
] as const;

export function lengthBucket(words: number): number {
  return LENGTH_BUCKETS.findIndex((b) => words >= b.min && words <= b.max);
}

export interface SentenceLengthView {
  sentences: Array<{ text: string; words: number; bucket: number }>;
  /** Sentences per bucket, same order as LENGTH_BUCKETS. */
  counts: number[];
  average: number;
  longest: number;
}

export function sentenceLengthView(text: string): SentenceLengthView {
  const sentences = splitSentenceTexts(text).filter((s) => s.words > 0).map((s) => ({ ...s, bucket: lengthBucket(s.words) }));
  const counts = LENGTH_BUCKETS.map(() => 0);
  for (const s of sentences) counts[s.bucket]++;
  const total = sentences.reduce((a, s) => a + s.words, 0);
  return { sentences, counts, average: sentences.length ? Math.round((total / sentences.length) * 10) / 10 : 0, longest: sentences.reduce((m, s) => Math.max(m, s.words), 0) };
}

// ---------------- Reading ease (Flesch) ----------------

export interface ReadingEase {
  /** English-style text only. false for non-Latin scripts, where the formula would mislead. */
  applicable: boolean;
  reason?: string;
  flesch: number;
  label: string;
  grade: number;
  gradeLabel: string;
  steps: CalcStep[];
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Flesch Reading Ease and Flesch-Kincaid grade, using the Word Counter's own sentence count (so the numbers on the page
 * agree) and the letters-only words of the text. The steps are built from the same numbers as the result.
 */
export function readingEase(text: string, sentenceCount: number): ReadingEase {
  const empty: ReadingEase = { applicable: false, flesch: 0, label: "", grade: 0, gradeLabel: "", steps: [] };
  const clean = text.replace(/https?:\/\/\S+|www\.\S+/g, " ");
  const words = clean.match(new RegExp("\\p{L}[\\p{L}'’-]*", "gu")) ?? [];
  const letters = (clean.match(new RegExp("\\p{L}", "gu")) ?? []).length;
  const latin = (clean.match(/[A-Za-zÀ-ɏ]/g) ?? []).length;
  if (words.length < 5 || sentenceCount < 1) return { ...empty, reason: "Add at least a full sentence (about 5 words) for a reading score." };
  if (letters > 0 && latin / letters < 0.7) return { ...empty, reason: "The reading score is built for English and similar Latin-script text, so it is hidden for this text." };
  const syllables = words.reduce((a, w) => a + countSyllables(w), 0);
  const wps = words.length / sentenceCount;
  const spw = syllables / words.length;
  const fleschRaw = 206.835 - 1.015 * wps - 84.6 * spw;
  const flesch = Math.max(0, Math.min(100, round1(fleschRaw)));
  const grade = Math.max(0, round1(0.39 * wps + 11.8 * spw - 15.59));
  const steps: CalcStep[] = [
    { label: "Words per sentence", formula: "Words (letters only, links left out) / sentences", inputs: { words: words.length, sentences: sentenceCount }, result: round1(wps) },
    { label: "Syllables per word", formula: "Estimated syllables / words (a rule-based estimate, so a few words are off)", inputs: { syllables, words: words.length }, result: Math.round(spw * 100) / 100 },
    { label: "Flesch Reading Ease", formula: "206.835 - 1.015 x words per sentence - 84.6 x syllables per word, kept between 0 and 100", inputs: { "words per sentence": round1(wps), "syllables per word": Math.round(spw * 100) / 100 }, result: flesch },
    { label: "Flesch-Kincaid grade", formula: "0.39 x words per sentence + 11.8 x syllables per word - 15.59, not below 0", inputs: { "words per sentence": round1(wps), "syllables per word": Math.round(spw * 100) / 100 }, result: grade },
  ];
  return { applicable: true, flesch, label: easeLabel(flesch), grade, gradeLabel: gradeLabel(grade), steps };
}
